import type { Attachment } from "@ark-bot/protocol";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { api } from "../api.js";
import {
  DICTATION_LANGUAGES,
  dictationLanguageInfo,
  dictationProblemText,
  dictationSettings,
  findDictation,
  placeHeard,
  setDictationAutoSend,
  setDictationLanguage,
  shellDictationAvailable,
  useDictationSettings,
  type DictationLanguage,
  type DictationProblem,
  type DictationSession,
  type DictationWindow,
} from "../dictation.js";
import { useText } from "../i18n.js";
import { loadDraft, saveDraft } from "../drafts.js";
import { editBetween, insertMention, matchMentionables, mentionAt, moveChips, snapOutOfChips, splitAtChips, widenDeletion, type Chip, type Mentionable } from "../mentions.js";

import { COMPOSER } from "../text/composer.js";

import { ActiveGlide } from "./ActiveGlide.js";
import type { AvatarFor } from "./Chat.js";

interface Props {
  readonly agentName: string;
  /** Who an @ can name in this chat (see mentions.ts); empty, and the @ list never opens. */
  readonly mentionables: readonly Mentionable[];
  readonly avatarFor: AvatarFor;
  readonly onSend: (text: string, attachments: Attachment[], replyTo?: string) => Promise<void>;
  /** The message the next one answers, shown as a chip over the field until sent or dismissed. */
  readonly replyTo?: { readonly id: string; readonly author: string; readonly preview: string } | null | undefined;
  readonly onClearReply?: (() => void) | undefined;
  /** Pictures can be attached; the tour's practice chat has nowhere to upload them, so it turns this off. */
  readonly acceptsPictures?: boolean;
  /** A message the box took back, put back in an empty field; a new `key` for each one. */
  readonly restore?: { readonly text: string; readonly key: number } | null;
  /** Told once `restore` is back in the field, so it is not put back again on the next visit. */
  readonly onRestored?: (() => void) | undefined;
  /** The chat this box writes to: what is typed and not sent stays with that chat (drafts.ts). None, and nothing is kept. */
  readonly draftKey?: string | undefined;
}

/** What the field holds: its text, the mention blocks over it, and the caret. */
interface FieldState {
  readonly text: string;
  readonly chips: readonly Chip[];
  readonly caret: number;
}

/** A dictation under way, one recogniser session of it at a time. */
interface Listening {
  readonly session: DictationSession;
  /** The field before the dictation began, for × to put back; the same through every session of one dictation. */
  readonly origin: FieldState;
  /** The field as this session's words last left it, for the next session to go on from. */
  last: FieldState;
  /** Whether the dictation, in any of its sessions, has heard words. */
  heard: boolean;
  /** Asked to stop so the words can go: auto-send's silence was up, or the mic was pressed with it on. */
  sendOnEnd: boolean;
}

/** Only pictures reach the model for now; anything else is left where it was. */
function pictures(files: Iterable<File>): File[] {
  return [...files].filter((file) => file.type.startsWith("image/"));
}

/**
 * The message box, as the prototype's: one rounded field with "+" to attach
 * on the left and an arrow to send on the right that appears once there is
 * something to send. Always open: a message while the agent works simply
 * reaches it (the turn in flight makes way for it), as in the prototype,
 * which has no separate stop or steer control. Pictures can be chosen,
 * pasted, or dropped on it. Typing @ opens a list of who can be mentioned
 * over the field, and it stays open through the spaces of a longer name:
 * arrows move, Enter or Tab picks, Escape closes. A picked
 * mention is one block (mentions.ts): a pill drawn under the field, stepped
 * over by the caret, and deleted whole. Where the device can listen
 * (dictation.ts), a mic beside Send puts spoken words at the caret as they
 * are heard; the field is read-only meanwhile, Send sends what is there,
 * and Escape or × takes the words back out.
 */
export function Composer({ agentName, mentionables, avatarFor, onSend, acceptsPictures = true, replyTo = null, onClearReply, restore = null, onRestored, draftKey }: Props): React.JSX.Element {
  const t = useText(COMPOSER);
  // Opened on a chat, the box starts with what was left in it (the app mounts one box per chat).
  const [initial] = useState(() => (draftKey == null ? null : loadDraft(draftKey)));
  const [text, setText] = useState(initial?.text ?? "");
  // A message the box took back comes back here to send again, unless something new is being written.
  const restoreKey = restore?.key ?? null;
  useEffect(() => {
    if (restore == null) return;
    setText((current) => (current.trim() === "" ? restore.text : current));
    onRestored?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per message given back, not per render
  }, [restoreKey]);
  /** The mentions picked from the list, as blocks over the text. */
  const [chips, setChips] = useState<readonly Chip[]>(initial?.chips ?? []);
  const [attachments, setAttachments] = useState<Attachment[]>(() => [...(initial?.attachments ?? [])]);
  // Every change is the chat's draft at once, so a switch to another bot, or a reload, loses nothing.
  useEffect(() => {
    if (draftKey != null) saveDraft(draftKey, { text, chips, attachments });
  }, [draftKey, text, chips, attachments]);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [caret, setCaret] = useState(0);
  const [focused, setFocused] = useState(false);
  const [active, setActive] = useState(0);
  /** Where the @ that Escape closed starts, so it stays closed until another is typed. */
  const [dismissed, setDismissed] = useState<number | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const field = useRef<HTMLTextAreaElement>(null);
  const list = useRef<HTMLUListElement>(null);
  /** The pills, drawn under the field in the same place as the text they sit behind. */
  const mirror = useRef<HTMLDivElement>(null);
  /** Where the caret was before this move, so a step into a block can cross it in the direction it was going. */
  const lastCaret = useRef(0);
  /** The caret to restore once a picked mention is in the field. */
  const placeCaret = useRef<number | null>(null);
  const ready = text.trim().length > 0 || attachments.length > 0;

  const dictation = useMemo(() => (typeof window === "undefined" ? null : findDictation(window as unknown as DictationWindow)), []);
  // In the app, the phone is asked whether it has a recogniser; a browser that has the API is taken at its word.
  const [canDictate, setCanDictate] = useState(dictation?.kind === "browser");
  useEffect(() => {
    if (dictation?.kind !== "shell") return;
    let live = true;
    void shellDictationAvailable(window as unknown as DictationWindow).then((available) => {
      if (live) setCanDictate(available);
    });
    return () => {
      live = false;
    };
  }, [dictation]);
  const { language, autoSend } = useDictationSettings();
  const listening = useRef<Listening | null>(null);
  const [isListening, setIsListening] = useState(false);
  const [languageMenu, setLanguageMenu] = useState(false);
  /** Why the last dictation ended early, until the field is next touched; worded when drawn, in the language of the moment. */
  const [dictationNote, setDictationNote] = useState<DictationProblem | null>(null);
  /**
   * Set when a dictation ends to be sent; the send happens on the render
   * after, so it sends the field as the last words left it.
   */
  const [sendHeard, setSendHeard] = useState(false);
  /** Auto-send's clock: running from the last new words until the chosen silence is up. */
  const quiet = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hush = (): void => {
    if (quiet.current != null) clearTimeout(quiet.current);
    quiet.current = null;
  };
  // A chat closed mid-sentence stops listening; its words go with it.
  useEffect(
    () => () => {
      hush();
      listening.current?.session.cancel();
    },
    [],
  );

  /** Stop listening so the words can go: the stop settles the last guess, and the end sends. */
  const finishAndSend = (): void => {
    hush();
    const current = listening.current;
    if (current == null) return;
    current.sendOnEnd = true;
    current.session.stop();
  };

  /**
   * New words (re)start the clock; with auto-send on, a silence as long as
   * Settings says finishes the dictation and sends it. Only where the engine
   * listens through pauses: elsewhere the device's own finish sends (onEnd).
   */
  const armQuiet = (): void => {
    hush();
    const { autoSend: on, autoSendDelay } = dictationSettings();
    if (!on || dictation?.listensThroughPauses !== true) return;
    quiet.current = setTimeout(() => {
      quiet.current = null;
      if (dictationSettings().autoSend) finishAndSend();
    }, autoSendDelay);
  };

  /**
   * Listens from the caret (or the edge of the block it is in); each hearing
   * replaces the last one's words there. `carry` goes on with a dictation
   * already under way (a language switch, or a recogniser that stopped at a
   * pause shorter than auto-send's): what it heard stays, and × still takes
   * all of it back.
   */
  const listen = (inLanguage: DictationLanguage, from: { readonly text: string; readonly chips: readonly Chip[]; readonly at: number }, carry?: Pick<Listening, "origin" | "heard">): void => {
    if (dictation == null) return;
    const at = snapOutOfChips(from.chips, from.at, from.at);
    const before = from.text.slice(0, at);
    const after = from.text.slice(at);
    setDictationNote(null);
    let own: Listening | null = null;
    const stopped = (): void => {
      setIsListening(false);
      setLanguageMenu(false);
    };
    const session = dictation.start(
      { language: inLanguage },
      {
        onHeard: ({ settled, guess }) => {
          if (own == null || listening.current !== own) return;
          const placed = placeHeard(before, after, `${settled} ${guess}`);
          // A guess settling as the same words is nothing new: the clock runs on.
          if (placed.text === own.last.text) return;
          const moved = moveChips(from.chips, { at: placed.at, removed: 0, inserted: placed.inserted });
          own.last = { text: placed.text, chips: moved, caret: placed.caret };
          if (placed.inserted > 0) {
            own.heard = true;
            armQuiet();
          }
          placeCaret.current = placed.caret;
          setChips(moved);
          setText(placed.text);
        },
        onEnd: (problem) => {
          // A session replaced (a language switch) or taken back ends quietly.
          if (own == null || listening.current !== own) return;
          listening.current = null;
          if (own.sendOnEnd) {
            stopped();
            if (own.heard) setSendHeard(true);
            return;
          }
          if (dictationSettings().autoSend && own.heard && (problem == null || problem === "no-speech")) {
            // A recogniser that listens through pauses stopped before auto-send's silence was up: listen on, the clock still running.
            if (dictation.listensThroughPauses && quiet.current != null) {
              listen(inLanguage, { text: own.last.text, chips: own.last.chips, at: own.last.caret }, { origin: own.origin, heard: true });
              return;
            }
            // Otherwise this end is the finish: the device's own, on Android, where the wait does not apply.
            hush();
            stopped();
            setSendHeard(true);
            return;
          }
          hush();
          stopped();
          if (problem != null) setDictationNote(problem);
        },
      },
    );
    const start = { text: from.text, chips: from.chips, caret: at };
    own = { session, origin: carry?.origin ?? start, last: start, heard: carry?.heard ?? false, sendOnEnd: false };
    listening.current = own;
    setIsListening(true);
  };

  /** Ends the dictation where it is, keeping whatever the field shows; the engine is told to stop hearing. */
  const letGo = (): void => {
    hush();
    const current = listening.current;
    if (current == null) return;
    listening.current = null;
    setIsListening(false);
    current.session.cancel();
  };

  const toggleDictation = (): void => {
    if (listening.current != null) {
      // Stop settles the last guess, which then arrives through onHeard before onEnd; with auto-send on, pressing the mic is being done.
      if (dictationSettings().autoSend) finishAndSend();
      else listening.current.session.stop();
      return;
    }
    listen(language, { text, chips, at: field.current?.selectionStart ?? text.length });
  };

  /** Escape or ×: everything this dictation heard comes back out, and the field is as it was. */
  const takeBack = (): void => {
    const current = listening.current;
    if (current == null) return;
    letGo();
    placeCaret.current = current.origin.caret;
    setChips(current.origin.chips);
    setText(current.origin.text);
  };

  /** Listening again from where the words so far end, so what was heard stays; for a change that needs a new session. */
  const relisten = (inLanguage: DictationLanguage): void => {
    const current = listening.current;
    if (current == null) return;
    letGo();
    listen(inLanguage, { text: current.last.text, chips: current.last.chips, at: current.last.caret }, { origin: current.origin, heard: current.heard });
    if (current.heard) armQuiet();
  };

  /** Another language, picked from the bar's menu: saved for next time, and listening goes on in it. */
  const pickLanguage = (next: DictationLanguage): void => {
    setLanguageMenu(false);
    if (next === language) return;
    setDictationLanguage(next);
    relisten(next);
  };

  /** Auto-send from the bar, saved as in Settings; turned on mid-dictation, the clock starts on what was heard already. */
  const toggleAutoSend = (): void => {
    setDictationAutoSend(!autoSend);
    if (!autoSend && listening.current?.heard === true) armQuiet();
    else hush();
  };

  useEffect(() => {
    if (!sendHeard) return;
    setSendHeard(false);
    void send({ dictated: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per dictation that ends with auto-send on, with the field as it now is
  }, [sendHeard]);

  // The language menu closes on a press anywhere outside it.
  const languagePicker = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!languageMenu) return;
    const away = (event: PointerEvent): void => {
      if (!languagePicker.current?.contains(event.target as Node)) setLanguageMenu(false);
    };
    document.addEventListener("pointerdown", away);
    return () => document.removeEventListener("pointerdown", away);
  }, [languageMenu]);
  const languageInfo = dictationLanguageInfo(language);

  // A block already picked is not asked about again when the caret sits at its end.
  const typing = focused && mentionables.length > 0 ? mentionAt(text, caret, mentionables) : null;
  const mention = typing != null && !chips.some((chip) => chip.start === typing.start) ? typing : null;
  const matches = mention != null && mention.start !== dismissed ? matchMentionables(mentionables, mention.query) : [];
  const open = matches.length > 0;
  const current = matches[Math.min(active, matches.length - 1)];

  // A new query starts at the top of its list; leaving the @ forgets that Escape closed it.
  useEffect(() => setActive(0), [mention?.start, mention?.query]);
  useEffect(() => {
    if (mention == null) setDismissed(null);
  }, [mention == null]);

  useEffect(() => {
    if (open) list.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: "nearest" });
  }, [open, active]);

  useLayoutEffect(() => {
    const at = placeCaret.current;
    if (at == null || field.current == null) return;
    placeCaret.current = null;
    field.current.setSelectionRange(at, at);
    setCaret(at);
  }, [text]);

  const pick = (row: Mentionable): void => {
    if (mention == null) return;
    const next = insertMention(text, mention.start, caret, row);
    placeCaret.current = next.caret;
    setChips([...moveChips(chips, next.edit), next.chip]);
    setText(next.text);
    field.current?.focus();
  };

  /** Follows the caret, so the list knows which @ it is under; a caret that lands inside a block is moved to its edge. */
  const track = (element: HTMLTextAreaElement): void => {
    let at = element.selectionStart;
    if (at === element.selectionEnd) {
      const snapped = snapOutOfChips(chips, at, lastCaret.current);
      if (snapped !== at) {
        element.setSelectionRange(snapped, snapped);
        at = snapped;
      }
    }
    lastCaret.current = at;
    setCaret(at);
  };

  /** A change typed, pasted or deleted: a deletion that reaches into a block takes the whole block, and the blocks move with the text. */
  const change = (element: HTMLTextAreaElement): void => {
    setDictationNote(null);
    const typed = editBetween(text, element.value, element.selectionStart);
    const edit = widenDeletion(chips, typed);
    setChips(moveChips(chips, edit));
    if (edit === typed) {
      setText(element.value);
      lastCaret.current = element.selectionStart;
      setCaret(element.selectionStart);
      return;
    }
    // Widened: the field lost part of a block, the text loses all of it.
    placeCaret.current = edit.at;
    lastCaret.current = edit.at;
    setText(text.slice(0, edit.at) + text.slice(edit.at + edit.removed));
  };

  // The pills lie exactly under the text: as wide as the field inside its scrollbar, as tall as it, scrolled with it.
  const fitMirror = (): void => {
    const element = field.current;
    const under = mirror.current;
    if (element == null || under == null) return;
    under.style.width = `${element.clientWidth}px`;
    under.style.height = `${element.clientHeight}px`;
    under.scrollTop = element.scrollTop;
  };
  useLayoutEffect(fitMirror);
  useEffect(() => {
    const element = field.current;
    if (element == null || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => fitMirror());
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // The field grows with the message, up to a few lines, then scrolls; empty, it is one line by its own style.
  useEffect(() => {
    const element = field.current;
    if (element == null) return;
    if (text.length === 0) {
      element.style.height = "";
      return;
    }
    element.style.height = "auto";
    element.style.height = `${Math.min(element.scrollHeight, 200)}px`;
  }, [text]);

  /**
   * The field empties the moment Send is pressed: the message is in the chat
   * already, greyed until the box has it (outbox.ts), so nobody waits on the
   * round trip with their words still in the box. A send that throws puts
   * them back, unless something new has been typed since. The field takes
   * focus after, to go on typing, except for a dictated message (auto-send,
   * or Send pressed while listening): on a phone focus is the keyboard, and
   * whoever spoke did not ask for it.
   */
  const send = async (options: { readonly dictated?: boolean } = {}): Promise<void> => {
    const trimmed = text.trim();
    if (trimmed.length === 0 && attachments.length === 0) return;
    const dictated = options.dictated === true || listening.current != null;
    // Sent mid-sentence: what the field shows goes, and nothing heard later lands in the emptied field.
    letGo();
    setDictationNote(null);
    const sent = { text, chips, attachments };
    const target = replyTo?.id;
    onClearReply?.();
    setText("");
    setChips([]);
    setAttachments([]);
    if (!dictated) field.current?.focus();
    try {
      await onSend(trimmed, sent.attachments, target);
    } catch {
      if ((field.current?.value ?? "").length > 0) return;
      setText(sent.text);
      setChips(sent.chips);
      setAttachments(sent.attachments);
    }
  };

  const attach = async (files: readonly File[]): Promise<void> => {
    if (!acceptsPictures || files.length === 0) return;
    setUploading(true);
    try {
      const uploaded: Attachment[] = [];
      for (const file of files) {
        const blob = await api.uploadBlob(file);
        uploaded.push({ blobId: blob.blobId, mimeType: blob.mimeType });
      }
      setAttachments((current) => [...current, ...uploaded]);
    } finally {
      setUploading(false);
      if (fileInput.current != null) fileInput.current.value = "";
    }
  };

  return (
    <div
      className={`composer${dragging ? " dragging" : ""}`}
      onDragOver={(event) => {
        if (!acceptsPictures || ![...event.dataTransfer.types].includes("Files")) return;
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        event.preventDefault();
        setDragging(false);
        void attach(pictures(event.dataTransfer.files));
      }}
    >
      {attachments.length > 0 ? (
        <div className="attachments">
          {attachments.map((attachment) => (
            <span key={attachment.blobId} className="attachment">
              <img src={api.blobUrl(attachment.blobId)} alt={t.attachment} />
              <button type="button" onClick={() => setAttachments((current) => current.filter((a) => a.blobId !== attachment.blobId))} title={t.remove} aria-label={t.removeAttachment}>
                ×
              </button>
            </span>
          ))}
        </div>
      ) : null}
      {open ? (
        <ul ref={list} id="mention-list" className="mention-list" role="listbox" aria-label={t.mention}>
          <ActiveGlide item selector=".mention-row.active" className="glide-hover glide-small" />
          {matches.map((row) => (
            <li
              key={row.id}
              id={`mention-${row.id}`}
              role="option"
              aria-selected={row === current}
              className={`mention-row${row === current ? " active" : ""}`}
              // Pressing a row must not take focus from the field, or the list would close under the pointer.
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => setActive(matches.indexOf(row))}
              onClick={() => pick(row)}
            >
              {row.everyone === true ? (
                <span className="mention-everyone" aria-hidden="true">
                  @
                </span>
              ) : (
                avatarFor(row.id, row.name, 28)
              )}
              <span className="mention-name">{row.everyone === true ? "@everyone" : row.name}</span>
              {row.detail.length > 0 ? <span className="mention-detail">{row.detail}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
      {replyTo != null ? (
        <div className="composer-reply">
          <span className="composer-reply-text">
            <span className="composer-reply-who">{t.replyingTo(replyTo.author)}</span>
            {replyTo.preview.length > 0 ? <span className="composer-reply-preview">{replyTo.preview}</span> : null}
          </span>
          <button type="button" className="icon composer-reply-clear" onClick={onClearReply} title={t.stopReplying} aria-label={t.stopReplying}>
            ×
          </button>
        </div>
      ) : null}
      {isListening ? (
        <div className="composer-listening" role="status">
          <span className="listening-dot" aria-hidden="true" />
          <span className="listening-text">{autoSend ? t.listeningAutoSend : t.listening}</span>
          <div ref={languagePicker} className="listening-picker">
            <button
              type="button"
              className="listening-chip"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => setLanguageMenu((open) => !open)}
              aria-haspopup="listbox"
              aria-expanded={languageMenu}
              title={t.dictatingIn(languageInfo.name)}
              aria-label={t.dictationLanguageIs(languageInfo.name)}
            >
              {languageInfo.label}
              <svg viewBox="0 0 10 10" width="8" height="8" aria-hidden="true">
                <path d="M2 3.5 5 6.5 8 3.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {languageMenu ? (
              <ul className="listening-languages" role="listbox" aria-label={t.dictationLanguage}>
                {DICTATION_LANGUAGES.map((entry) => (
                  <li
                    key={entry.tag}
                    role="option"
                    aria-selected={entry.tag === language}
                    className={`listening-language-row${entry.tag === language ? " chosen" : ""}`}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => pickLanguage(entry.tag)}
                  >
                    <span className="listening-language-label">{entry.label}</span>
                    <span className="listening-language-name">{entry.name}</span>
                    {entry.tag === language ? (
                      <svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true">
                        <path d="M2.5 6.2 5 8.5 9.5 3.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
          <button
            type="button"
            className={`listening-chip${autoSend ? " on" : ""}`}
            onMouseDown={(event) => event.preventDefault()}
            onClick={toggleAutoSend}
            aria-pressed={autoSend}
            title={autoSend ? t.autoSendOn : t.autoSendOff}
          >
            {t.autoSend}
          </button>
          <button type="button" className="icon composer-listening-cancel" onClick={takeBack} title={t.takeBackTitle} aria-label={t.takeBack}>
            ×
          </button>
        </div>
      ) : dictationNote != null ? (
        <div className="composer-note" role="status">
          <span className="composer-note-text">{dictationProblemText(dictationNote)}</span>
          <button type="button" className="icon composer-note-clear" onClick={() => setDictationNote(null)} title={t.dismiss} aria-label={t.dismiss}>
            ×
          </button>
        </div>
      ) : null}
      <div className="composer-field">
        {acceptsPictures ? (
          <>
            <input ref={fileInput} type="file" accept="image/*" multiple hidden onChange={(event) => void attach(pictures(event.target.files ?? []))} />
            <button type="button" className="icon attach" onClick={() => fileInput.current?.click()} disabled={uploading} title={t.attachPicture} aria-label={t.attachPicture}>
              {uploading ? "…" : "+"}
            </button>
          </>
        ) : null}
        {/* With a block in it, the text is drawn by the layer underneath, so a
            block can wear its agent's face over its @; the field on top keeps
            the caret, the selection and every edit, its own text invisible. */}
        <div className={`composer-input${chips.length > 0 ? " drawn" : ""}`}>
          {chips.length > 0 ? (
            <div ref={mirror} className="composer-mirror" aria-hidden="true">
              {splitAtChips(text, chips).map((segment, index) => {
                if (!segment.mention) return <span key={index}>{segment.text}</span>;
                const row = segment.row;
                return (
                  <mark key={index} className="composer-chip">
                    {row != null && row.everyone !== true && segment.text.startsWith("@") ? (
                      <>
                        <span className="chip-at">
                          @<span className="chip-face">{avatarFor(row.id, row.name, 14)}</span>
                        </span>
                        {segment.text.slice(1)}
                      </>
                    ) : (
                      segment.text
                    )}
                  </mark>
                );
              })}
              {"​"}
            </div>
          ) : null}
        <textarea
          ref={field}
          value={text}
          onChange={(event) => change(event.target)}
          onScroll={fitMirror}
          onSelect={(event) => track(event.currentTarget)}
          onFocus={(event) => {
            setFocused(true);
            track(event.currentTarget);
          }}
          onBlur={() => setFocused(false)}
          onPaste={(event) => {
            const files = pictures([...event.clipboardData.items].flatMap((item) => (item.kind === "file" ? [item.getAsFile()].filter((file): file is File => file != null) : [])));
            if (files.length === 0) return;
            event.preventDefault();
            void attach(files);
          }}
          onKeyDown={(event) => {
            if (open && current != null && !event.nativeEvent.isComposing) {
              if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                event.preventDefault();
                const step = event.key === "ArrowDown" ? 1 : -1;
                setActive((matches.indexOf(current) + step + matches.length) % matches.length);
                return;
              }
              if ((event.key === "Enter" && !event.shiftKey) || event.key === "Tab") {
                event.preventDefault();
                pick(current);
                return;
              }
              if (event.key === "Escape") {
                event.preventDefault();
                setDismissed(mention?.start ?? null);
                return;
              }
            }
            if (event.key === "Escape" && languageMenu) {
              event.preventDefault();
              setLanguageMenu(false);
              return;
            }
            if (event.key === "Escape" && listening.current != null) {
              event.preventDefault();
              takeBack();
              return;
            }
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              void send();
            }
          }}
          readOnly={isListening}
          placeholder={dragging ? t.dropPicture : t.messageTo(agentName)}
          aria-label={t.messageTo(agentName)}
          aria-autocomplete={mentionables.length > 0 ? "list" : undefined}
          aria-controls={open ? "mention-list" : undefined}
          aria-activedescendant={open && current != null ? `mention-${current.id}` : undefined}
          rows={1}
        />
        </div>
        {canDictate ? (
          <button
            type="button"
            className={`icon mic${isListening ? " listening" : ""}`}
            // The field keeps focus (and a phone its keyboard) when the mic is pressed.
            onMouseDown={(event) => event.preventDefault()}
            onClick={toggleDictation}
            aria-pressed={isListening}
            aria-label={isListening ? (autoSend ? t.stopDictatingAndSend : t.stopDictating) : t.dictate}
            title={isListening ? (autoSend ? t.stopDictatingAndSend : t.stopDictatingKeep) : t.dictateIn(languageInfo.name)}
          >
            <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
              <rect x="7" y="2.5" width="6" height="10" rx="3" fill={isListening ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.8" />
              <path d="M4.5 9.5a5.5 5.5 0 0 0 11 0M10 15v2.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </button>
        ) : null}
        <button type="button" className={`icon send${ready ? " ready" : ""}`} onClick={() => void send()} disabled={!ready || uploading} aria-label={t.send} title={t.sendTitle}>
          <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
            <path d="M10 16V4M4.5 9.5 10 4l5.5 5.5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}
